import { useState, useRef, useEffect, type JSX } from "react";
import { Button, Popover, Text } from "../DesignSystem";
import { Calendar, ChevronLeft, ChevronRight, Edit } from "lucide-react";
import { twMerge } from "tailwind-merge";
import DateUtil, { DAYS_OF_WEEK, FULL_MONTHS } from "../../util/DateUtil";
import { useCurrentScreenSize } from "./Hooks/useCurrentScreenSize";

interface DatePickerProps {
    value?: Date | null;
    onChange?: (date: Date | null) => void;
    placeholder?: string;
    customTriggerText?: string | JSX.Element;
    showPreviousMonthDays?: boolean;
    calendarClassName?: string;
    triggerClassName?: string;
    /** Earliest selectable day. Days before it render disabled. */
    minDate?: Date | null;
    /** Latest selectable day. Days after it render disabled. */
    maxDate?: Date | null;
    /** Hides the Clear button where the field is required. */
    allowClear?: boolean;
    /** Which side of the trigger the calendar opens on, on desktop. */
    side?: "right" | "bottom";
    /** "inline" is a click-to-edit property with a hover pencil; "field" is a plain form input. */
    appearance?: "inline" | "field";
}

const StyledDatePicker = ({
    value,
    onChange,
    placeholder = "Select date",
    customTriggerText,
    showPreviousMonthDays = false,
    calendarClassName = "",
    triggerClassName,
    minDate,
    maxDate,
    allowClear = true,
    side = "right",
    appearance = "inline",
}: DatePickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(value || new Date());
    const { isDesktop } = useCurrentScreenSize();

    // The Popover panel supplies the border, background and shadow. The calendar is taller than the
    // panel's default 24rem cap, so it only caps at the space left on screen, and spaces its own sections.
    const calendarClass = twMerge("w-[98vw] sm:w-[340px] max-h-(--available-height) gap-0 p-5", calendarClassName);

    const getDaysInMonth = (date: Date) => {
        // By setting day to 0, we get the last day of the previous month.
        // So to get the last day of the current month, we set month + 1 and day 0
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const handlePrevMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    // Bounds are compared at day granularity so a time-of-day on the bound doesn't exclude the bound's own day.
    const dayStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const minTime = minDate !== null && minDate !== undefined ? dayStart(minDate) : null;
    const maxTime = maxDate !== null && maxDate !== undefined ? dayStart(maxDate) : null;

    const isOutOfRange = (date: Date) => {
        const time = dayStart(date);
        if (minTime !== null && time < minTime) {
            return true;
        }
        if (maxTime !== null && time > maxTime) {
            return true;
        }
        return false;
    };

    const isSelectedDate = (day: number) => {
        if (!value) return false;
        return value.getDate() === day && value.getMonth() === viewDate.getMonth() && value.getFullYear() === viewDate.getFullYear();
    };

    const isToday = (day: number) => {
        const today = new Date();
        return today.getDate() === day && today.getMonth() === viewDate.getMonth() && today.getFullYear() === viewDate.getFullYear();
    };

    // Render both previous month's days and current month's days
    const renderDays = () => {
        const daysInMonth = getDaysInMonth(viewDate);
        const firstDay = getFirstDayOfMonth(viewDate);
        const daysInPrevMonth = getDaysInMonth(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
        const days: JSX.Element[] = [];

        const todayClass = "border-2 border-bronze-300 rounded";
        const selectedDayClass = "";

        // Days from previous month
        for (let i = firstDay - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            const prevMonthDate = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, day);
            const prevOutOfRange = isOutOfRange(prevMonthDate);
            days.push(
                showPreviousMonthDays ? (
                    // Show previous month's days if enabled
                    <Button
                        key={`prev-month-${day}`}
                        onClick={() => {
                            onChange?.(prevMonthDate);
                            setIsOpen(false);
                        }}
                        disabled={prevOutOfRange}
                        color="clear"
                        text={
                            <Text variant="body500" className={prevOutOfRange ? "text-stone-300" : "text-neutral-400"}>
                                {day}
                            </Text>
                        }
                        className="size-8 justify-center p-0"
                    />
                ) : (
                    // Empty placeholder for previous month's days when not enabled
                    <div key={`prev-month-empty-${i}`}></div>
                ),
            );
        }

        // Days of the current month
        for (let day = 1; day <= daysInMonth; day++) {
            const selected = isSelectedDate(day);
            const today = isToday(day);
            const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
            const outOfRange = isOutOfRange(date);

            let dayTextClass = "text-stone-950";
            if (selected) {
                dayTextClass = "text-white";
            } else if (outOfRange) {
                dayTextClass = "text-stone-300";
            }

            days.push(
                <Button
                    key={`current-month-${day}`}
                    onClick={() => {
                        onChange?.(date);
                        setIsOpen(false);
                    }}
                    disabled={outOfRange}
                    color={selected ? "bronze" : "clear"}
                    text={
                        <Text variant="body500" className={dayTextClass}>
                            {day}
                        </Text>
                    }
                    className={twMerge("size-8 justify-center p-0", selected && selectedDayClass, !selected && today && todayClass)}
                />,
            );
        }

        return days;
    };

    const triggerText = value ? (customTriggerText ?? DateUtil.formatted(value)) : placeholder;

    const trigger =
        appearance === "field" ? (
            <Button
                icon={<Calendar />}
                text={triggerText}
                color="clear"
                className={twMerge("group h-8 max-w-full overflow-hidden px-2 hover:bg-white active:bg-white", triggerClassName)}
                textClassName={`font-normal ${value ? "text-stone-950" : "text-stone-400"}`}
            />
        ) : (
            <Button
                icon={<Calendar />}
                text={triggerText}
                color="clear"
                className={twMerge("group h-8 max-w-full overflow-hidden px-1 hover:bg-stone-100", isOpen && "bg-stone-100", triggerClassName)}
                textClassName={`font-medium ${value ? "text-stone-950" : "text-stone-600"}`}
                rightIcon={
                    <Edit className={`h-4 w-4 stroke-stone-400 transition-opacity duration-[0] group-hover:opacity-100 ${!isOpen && "opacity-0"}`} />
                }
            />
        );

    return (
        <Popover
            trigger={trigger}
            open={isOpen}
            onOpenChange={setIsOpen}
            side={isDesktop ? side : "bottom"}
            align="start"
            ariaLabel="Choose a date"
            className={calendarClass}
        >
            {/* Header with month/year navigation */}
            <div className="mb-4 flex items-center justify-between">
                <Button onClick={handlePrevMonth} color="clear" icon={<ChevronLeft className="size-4 stroke-stone-950" />} />
                <Text variant="largeSectionHeading600">
                    {FULL_MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                </Text>
                <Button onClick={handleNextMonth} color="clear" icon={<ChevronRight className="size-4 stroke-stone-950" />} />
            </div>

            {/* Day of week headers */}
            <div className="mb-3 grid grid-cols-7 gap-2">
                {DAYS_OF_WEEK.map(day => (
                    <div key={day} className="flex items-center justify-center">
                        <Text variant={"body700"}>{day}</Text>
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="mb-2 grid grid-cols-7 gap-2">{renderDays()}</div>

            {/* Clear button */}
            {allowClear && (
                <Button
                    onClick={() => {
                        onChange?.(null);
                        setIsOpen(false);
                    }}
                    color="clear"
                    text="Clear"
                />
            )}
        </Popover>
    );
};

export default StyledDatePicker;
